import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { UtilsService } from '../../shared/utils.service';
import { RBACService } from '../../shared/rbac.service';
import { CustomDataSource } from '../../utils/custom-data-source';
import { SitesCodesClaimComponent } from './codes-claim/sites-codes-claim.component';
import { SitesCodesFilterComponent } from './sites-codes-filter/sites-codes-filter.component';
import { CodeModel } from '../../models/code.model';

@Component({
  selector: 'app-codes',
  templateUrl: './sites-codes.component.html',
  styleUrls: ['./sites-codes.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesCodesComponent implements OnInit, AfterViewInit {
  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'id',
    'site',
    'name',
    'feature',
    'type',
    'date_created',
    // 'date_created_UTC',
    'actions'
  ];
  dataSource = new CodesDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  parent_id: number;
  child_id: number;
  site_id: number;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate permission lookups on every change detection cycle.
  canView: boolean = false;
  canCreate: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private dialog: MatDialog,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve the RBAC permission flags first so they are always set, even when
    // the access guard below short circuits the rest of this method.
    // These mirror the API guards in SiteCodesController: index needs qr:view, store needs
    // qr:create, update needs qr:view + qr:edit, destroy needs qr:view + qr:delete. sites:view is
    // additionally required by the API on all of them and is enforced by the redirect below, so it
    // is not repeated in every flag.
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // Check permissions - user must have sites:view AND any QR codes permission (view, create, edit, or delete)
    const hasSitesView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );

    // Use OR logic (exact: false) - user needs ANY QR code permission to access the page
    const hasViewOrCreateQRCodePermission = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      false
    );

    if (!hasSitesView || !hasViewOrCreateQRCodePermission) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access QR codes.'
      );
      this.router.navigate(['/sites']);
      return;
    }

    // Hide checkbox column if user doesn't have delete permission
    // Checkbox column is only used for bulk delete operations
    if (!this.canDelete) {
      this.displayedColumns = this.displayedColumns.filter(
        (col) => col !== 'select'
      );
    }

    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if (
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;
      // Add the site id as the parent id of the data source.
      this.dataSource.site_id = this.site_id;
      this.dataSource.site_ids.push(this.dataSource.site_id);

      // Only load data if user has view permission
      if (this.canView) {
        // Get data from the data source.
        this.dataSource.getData();
      }
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC dates by it's corresponding date field.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.sort_by = 'date_created';
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  claimCode() {
    // Check permissions - user must have sites-qr-codes:create
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_CREATE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to claim QR codes.'
      );
      return;
    }

    const dialogRef = this.dialog.open(SitesCodesClaimComponent, {
      width: '320px',
      data: { site_id: this.dataSource.site_id }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dataSource.getData();
      }
    });
  }

  onRemove(code: CodeModel) {
    // Check permissions - user must have sites-qr-codes:view AND sites-qr-codes:delete,
    // matching SiteCodesController@destroy.
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_DELETE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete QR codes.'
      );
      return;
    }

    this.utils.showModal(
      'Remove QR Code',
      'Are you sure you want to remove this QR code?',
      () => {
        this.api
          .makeRequest(
            'delete',
            'v1/sites/' + code.site_id + '/qrcodes/' + code.id
          )
          .then((response: any) => {
            this.utils.showToast('The QR code was removed.');
            this.dataSource.selection.deselect(code.id);
            this.dataSource.getData();
          });
      }
    );
  }

  onDeleteSelected() {
    // Check permissions - user must have sites-qr-codes:view AND sites-qr-codes:delete,
    // matching SiteCodesController@destroy. sites:view is enforced by the ngOnInit redirect.
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_DELETE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete QR codes.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Selected QR Codes',
      'Are you sure you want to remove the selected QR codes?',
      () => {
        this.api
          .makeRequest(
            'delete',
            'v1/sites/' +
              this.dataSource.site_id +
              '/qrcodes/' +
              this.dataSource.selection.selected.join(',')
          )
          .then((response: any) => {
            this.utils.showToast('The QR code was removed.');
            this.dataSource.selection.clear();
            this.dataSource.getData();
          });
      }
    );
  }

  onFilter() {
    this.utils.showComponentDialog(
      SitesCodesFilterComponent,
      {
        selected_site_ids: this.dataSource.site_ids,
        parent_site_id: this.parent_id
      },
      {
        width: '350px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.site_ids = results.site_ids;

          if (this.dataSource.site_ids.length === 0) {
            this.dataSource.site_ids.push(this.site_id);
          }

          this.dataSource.getData();
        }
      }
    );
  }

  protected readonly RBACService = RBACService;
}

/**
 * The data source to get the list of QR codes for the site.
 */
export class CodesDataSource extends CustomDataSource {
  site_id = 0;

  site_ids: number[] = [];

  // record sorting and direction
  sort_by = 'name';
  sort_order = 'asc';

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI(
      'sites/' + this.site_id + '/qrcodes',
      resetOffset,
      () => {},
      {
        site_ids: this.site_ids.join(',')
      }
    );
  }
}
